import { useCallback, useEffect } from 'react';

import { useShowCommentsPanel } from '@confluence/comments-panel';
import { useCommentsPanelActions, useCommentsPanelScroll } from '@confluence/comments-panel-utils';
import { useCommentService } from '@confluence/content-types-bridge-comments';

export const useRegisterCommentsPanelCallbacks = () => {
	const { commentService } = useCommentService();
	const { isCommentsPanelShown, showCommentsPanel, hideCommentsPanel } = useShowCommentsPanel();
	const { setCurrentlySelectedThreadKey } = useCommentsPanelActions();
	const { scrollCommentsPanelToThread } = useCommentsPanelScroll();

	const setCommentThreadHandler = useCallback(
		(threadId?: string) => {
			// highlight a comment in sidebar
			// if threadId is undefined, it means we are de-selecting
			setCurrentlySelectedThreadKey(threadId);
			if (threadId) {
				// scroll to the comment thread in the sidebar
				scrollCommentsPanelToThread({
					annotationId: threadId,
					annotationClicked: true,
				});
			}
		},
		[setCurrentlySelectedThreadKey, scrollCommentsPanelToThread],
	);

	const showPanelHandler = useCallback(
		(threadId?: string) => {
			if (!isCommentsPanelShown) {
				showCommentsPanel();
			}
			if (threadId) {
				setCommentThreadHandler(threadId);
			}
		},
		[isCommentsPanelShown, showCommentsPanel, setCommentThreadHandler],
	);

	const hidePanelHandler = useCallback(() => {
		if (isCommentsPanelShown) {
			hideCommentsPanel();
		}
	}, [isCommentsPanelShown, hideCommentsPanel]);

	useEffect(() => {
		commentService.registerSelectCommentThreadHandler(setCommentThreadHandler);
		commentService.registerShowPanelHandler(showPanelHandler);
		commentService.registerHidePanelHandler(hidePanelHandler);
		return () => {
			// un-set the handlers on component unmount
			commentService.registerSelectCommentThreadHandler(undefined);
			commentService.registerShowPanelHandler(undefined);
			commentService.registerHidePanelHandler(undefined);
		};
	}, [commentService, showPanelHandler, hidePanelHandler, setCommentThreadHandler]);
};
